import { useNavigate } from 'react-router-dom';
import { Card, Spin, Typography, Tag, Empty } from 'antd';
import {
  CalendarOutlined,
  BankOutlined,
  BellOutlined,
  HistoryOutlined,
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  UploadOutlined,
  CalendarTwoTone,
  DownloadOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { useDashboard } from '../../contexts/DashboardContext';
import { useAuth } from '../../contexts/AuthContext';
import { markRequestRead } from '../../api/io-requests';
import { markIoPlanMentionRead } from '../../api/dashboard';
import type { DashboardIO, PendingItem } from '../../types/dashboard';
import { decodeHtml } from '../../lib/planningCycleLabels';
import { formatTimeAgo, formatDate, formatDateTime, fmtK, normalizeOpexCapexWordsInText } from '../../utils/formatters';
import { buildOutlookCalendarUrl } from '../../utils/calendarLinks';
import { usePageTitle } from '../../hooks/usePageTitle';
import { prefetchRoute } from '../../utils/routePrefetch';
import { hasBridgeAccess } from '../../utils/permissions';

const { Title, Text } = Typography;

const announcementGradients: Record<string, string> = {
  info: 'linear-gradient(135deg, #1B6AEE 0%, #6366f1 100%)',
  warning: 'linear-gradient(135deg, #E6A700 0%, #f59e0b 100%)',
  success: 'linear-gradient(135deg, #00A86B 0%, #10b981 100%)',
  urgent: 'linear-gradient(135deg, #DB0A40 0%, #ef4444 100%)',
};

// Warning gradient is amber (#E6A700) — white text contrast ~2.25:1 (WCAG fail).
// Use dark text for warning; all other types are dark enough for white.
const announcementTextColor = (type: string) => type === 'warning' ? '#1a1a1a' : 'white';

const activityIcons: Record<string, React.ReactNode> = {
  create: <PlusCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />,
  update: <EditOutlined style={{ color: '#1677ff', marginRight: 6 }} />,
  delete: <DeleteOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />,
  copy: <CopyOutlined style={{ color: '#722ed1', marginRight: 6 }} />,
  import: <UploadOutlined style={{ color: '#d48806', marginRight: 6 }} />,
};

/** Bridge column: signed $K delta with direction, or — when null/0. */
function renderBridgeDelta(io: DashboardIO) {
  const delta = io.bridge_delta;
  if (delta == null || delta === 0) {
    return <Text style={{ color: '#8c8c8c' }}>—</Text>;
  }
  const increasing = delta > 0;
  const color = increasing ? 'var(--rb-color-accent-positive, #12873F)' : 'var(--rb-color-accent-negative, #DB0A40)';
  const Arrow = increasing ? ArrowUpOutlined : ArrowDownOutlined;
  const direction = increasing ? 'up' : 'down';
  return (
    <Text style={{ color }} aria-label={`Bridge ${direction} ${fmtK(Math.abs(delta))}`}>
      <Arrow style={{ fontSize: 12, marginRight: 4 }} aria-hidden />
      {fmtK(Math.abs(delta))}
    </Text>
  );
}

const pendingActionLabels: Record<string, string> = {
  no_plan: 'View IO',
  missing_comment: 'View IO',
  io_plan_blocked: 'Open IO',
  io_plan_resubmit: 'Revise Plan',
  io_plan_mention: 'View Comment',
  io_plan_awaiting_lock: 'Finance Queue',
  bi_mismatch_post_close: 'Review mismatches',
  draft_io: 'Manage IOs',
  draft_gl: 'Manage GLs',
  io_request_awaiting_manager: 'Review',
  io_request_awaiting_finance: 'Finance Queue',
  io_request_rejected: 'View',
  io_request_comment: 'View Comment',
  te_plan_awaiting_approval: 'Review Plan',
  te_plan_awaiting_finance: 'Finance Queue',
  te_plan_rejected: 'Revise Plan',
};

// User-facing chip text for each Needs Attention item type. Keep these short —
// they render inside an antd Tag next to the message line. Falls back to the
// raw type (underscores → spaces) for any unmapped/legacy type.
const pendingTagLabels: Record<string, string> = {
  no_plan: 'No plan',
  missing_comment: 'Missing comment',
  io_plan_blocked: 'Action needed',
  io_plan_resubmit: 'Revise & re-submit',
  io_plan_mention: 'You were mentioned',
  io_plan_awaiting_lock: 'Awaiting lock',
  bi_mismatch_post_close: 'BI mismatch after close',
  draft_io: 'Draft IO',
  draft_gl: 'Draft GL',
  io_request_awaiting_manager: 'Awaiting your approval',
  io_request_awaiting_finance: 'Awaiting Finance',
  io_request_rejected: 'Rejected',
  io_request_comment: 'New comment',
  te_plan_awaiting_approval: 'Awaiting your approval',
  te_plan_awaiting_finance: 'Awaiting Finance',
  te_plan_rejected: 'Plan rejected',
};

const IO_REQUEST_DEEP_LINK_TYPES = new Set([
  'io_request_awaiting_manager',
  'io_request_rejected',
  'io_request_comment',
]);

function pendingActionLabel(item: PendingItem): string | undefined {
  if (item.type === 'io_plan_mention') {
    return item.comment_kind === 'line_item' ? 'View line item' : 'View IO comment';
  }
  return pendingActionLabels[item.type];
}

/** My IOs rows are scoped to the open BP cycle — always pass cycle= in the deep link. */
function myIoPlanPath(io: DashboardIO, openBpCycleId: number): string {
  const params = new URLSearchParams({
    cycle: String(openBpCycleId),
    io: String(io.id),
    expand: '1',
    attention: '1',
  });
  return `/planning/io-plans?${params.toString()}`;
}

/** Bridge cell deep link — Bridges page pre-filtered to this IO in the open BP cycle. */
function bridgeDeepLinkPath(io: DashboardIO, openBpCycleId: number): string {
  const params = new URLSearchParams({ cycle: String(openBpCycleId), io: String(io.id) });
  return `/planning/bridges?${params.toString()}`;
}

/** One Pagers cell deep link — One-Pagers page pre-filtered to this IO in the open BP cycle. */
function onePagerDeepLinkPath(io: DashboardIO, openBpCycleId: number): string {
  const params = new URLSearchParams({ cycle: String(openBpCycleId), io: String(io.id) });
  return `/planning/one-pagers?${params.toString()}`;
}

/**
 * Accessible link for a single My IOs cell. Only specific cells navigate (the
 * IO name and status go to the IO plan; bridge/one-pager cells go to their own
 * pages) — the row itself is not clickable. Propagation is stopped so cells
 * never interfere with each other.
 */
function MyIoCellLink({
  to,
  ariaLabel,
  align = 'left',
  onActivate,
  children,
}: {
  to: string;
  ariaLabel: string;
  align?: 'left' | 'right';
  onActivate: (to: string) => void;
  children: React.ReactNode;
}) {
  return (
    <a
      href={to}
      className="my-io-cell-link"
      aria-label={ariaLabel}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onActivate(to);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate(to);
        }
      }}
      style={{ textAlign: align }}
    >
      {children}
    </a>
  );
}

function pendingItemPath(item: PendingItem): string | null {
  const cycle = item.cycle_id;
  const io = item.io_id;
  switch (item.type) {
    case 'no_plan':
    case 'missing_comment':
    case 'io_plan_blocked':
    case 'io_plan_resubmit':
      return cycle && io
        ? `/planning/io-plans?cycle=${cycle}&io=${io}&expand=1&attention=1`
        : '/planning/io-plans';
    case 'io_plan_mention':
      if (cycle && io) {
        if (item.comment_kind === 'line_item' && item.line_item_id) {
          return `/planning/io-plans?cycle=${cycle}&io=${io}&focus=line-item&line_item_id=${item.line_item_id}&expand=1&attention=1`;
        }
        return item.source_id
          ? `/planning/io-plans?cycle=${cycle}&io=${io}&focus=comment&expand=1&source_id=${item.source_id}&attention=1`
          : `/planning/io-plans?cycle=${cycle}&io=${io}&focus=comment&expand=1&attention=1`;
      }
      return '/planning/io-plans';
    case 'io_plan_awaiting_lock':
      return cycle
        ? `/planning/io-plans?cycle=${cycle}&status=submitted&attention=1`
        : '/planning/io-plans';
    case 'bi_mismatch_post_close':
      return cycle
        ? `/planning/io-plans?cycle=${cycle}&status=bi_mismatch&attention=1`
        : '/planning/io-plans';
    case 'io_request_awaiting_finance':
      return '/planning/io-requests?view=finance';
    case 'io_request_awaiting_manager':
    case 'io_request_rejected':
    case 'io_request_comment':
      return item.request_id ? `/planning/io-requests/${item.request_id}` : '/planning/io-requests';
    case 'draft_io':
      return '/admin/ios';
    case 'draft_gl':
      return '/admin/gls';
    case 'te_plan_awaiting_approval':
    case 'te_plan_rejected':
      return item.plan_id ? `/planning/te?plan_id=${item.plan_id}` : '/planning/te';
    case 'te_plan_awaiting_finance':
      return '/planning/te';
    default:
      return null;
  }
}

export default function DashboardPage() {
  usePageTitle('Dashboard');
  const navigate = useNavigate();
  const { data, loading, refreshing } = useDashboard();
  const { currentUser } = useAuth();
  const bridgesAccessible = hasBridgeAccess(currentUser);

  const goTo = (path: string) => {
    prefetchRoute(path);
    navigate(path);
  };

  if (loading && !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) return <Empty description="Failed to load dashboard" />;

  return (
    <div
      aria-busy={refreshing}
      style={{
        padding: 24,
        maxWidth: 1400,
        margin: '0 auto',
        height: 'calc(100vh - 106px)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        opacity: refreshing ? 0.92 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      {/* Announcements */}
      {data.announcements?.map((a) => {
        const bg = announcementGradients[a.announcement_type] || announcementGradients.info;
        return (
          <Card
            key={a.id}
            style={{ marginBottom: 12, background: bg, color: announcementTextColor(a.announcement_type), borderRadius: 12, border: 'none' }}
            styles={{ body: { padding: '16px 20px' } }}
          >
            <Title level={5} style={{ color: announcementTextColor(a.announcement_type), margin: 0 }}>{a.title}</Title>
            <Text style={{ color: announcementTextColor(a.announcement_type) }}>{normalizeOpexCapexWordsInText(a.message)}</Text>
          </Card>
        );
      })}

      {/* Top row (max 45% of viewport) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 20,
          marginTop: 16,
          maxHeight: '45%',
          minHeight: 0,
        }}
      >
        {/* Key Dates */}
        <Card
          title={<><CalendarOutlined /> Key Dates</>}
          style={{ borderRadius: 12, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
          styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto' } }}
        >
          {data.upcoming_dates?.length || data.later_dates?.length ? (
            [...(data.upcoming_dates || []), ...(data.later_dates || [])].slice(0, 8).map((d) => {
              const eventDate = new Date(d.event_date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const daysUntil = Math.ceil((eventDate.getTime() - today.getTime()) / 86400000);
              return (
                <div key={d.id} style={{ padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>{d.title}</Text>
                    <br />
                    <Text style={{ fontSize: 14, color: '#595959' }}>
                      {formatDate(d.event_date)}
                      {daysUntil >= 0 && <span> · {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}</span>}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <a
                      href={buildOutlookCalendarUrl({ title: d.title, eventDate: d.event_date, description: d.description })}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Add to Outlook Calendar"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#1677ff', fontSize: 14 }}
                    >
                      <CalendarTwoTone />
                    </a>
                    <a
                      href={`/api/apex/dates/${d.id}/ics`}
                      download
                      title="Download ICS"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#1677ff', fontSize: 14 }}
                    >
                      <DownloadOutlined />
                    </a>
                    <Tag color={d.event_type === 'deadline' ? 'red' : d.event_type === 'opens' ? 'green' : 'blue'}>
                      {d.event_type}
                    </Tag>
                  </div>
                </div>
              );
            })
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No upcoming dates" />
          )}
        </Card>

        {/* My IOs — scoped to the open BP cycle only */}
        <Card
          title={
            <>
              <BankOutlined /> My IOs
              {data.current_cycle && <Text style={{ fontSize: 14, marginLeft: 8, color: '#595959' }}>- {data.current_cycle.cycle_type}{String(data.current_cycle.fiscal_year).slice(-2)}</Text>}
            </>
          }
          extra={
            data.current_cycle && data.my_ios?.length ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {data.my_ios.length} IO{data.my_ios.length !== 1 ? 's' : ''}
              </Text>
            ) : null
          }
          style={{ borderRadius: 12, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
          styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto' } }}
        >
          {!data.current_cycle ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No open Budget Plan" />
          ) : data.my_ios?.length ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1.4fr) 90px 90px 72px 72px',
                  gap: 8,
                  padding: '0 0 8px',
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                <Text style={{ fontSize: 12, color: '#8c8c8c' }}>IO</Text>
                <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Status</Text>
                <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Bridge</Text>
                <Text style={{ fontSize: 12, color: '#8c8c8c', textAlign: 'right' }}>One Pagers</Text>
                <Text style={{ fontSize: 12, color: '#8c8c8c', textAlign: 'right' }}>Total</Text>
              </div>
              {data.my_ios.map((io) => {
                const ioPlanPath = myIoPlanPath(io, data.current_cycle!.id);
                return (
                <div
                  key={io.id}
                  style={{
                    padding: '10px 0',
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1.4fr) 90px 90px 72px 72px',
                    gap: 8,
                    alignItems: 'center',
                    borderRadius: 6,
                  }}
                >
                  <MyIoCellLink
                    to={ioPlanPath}
                    ariaLabel={`Open IO plan for ${io.order}`}
                    onActivate={goTo}
                  >
                    <span style={{ display: 'block', minWidth: 0 }}>
                      <Text strong>{io.order}</Text>
                      {io.is_owner && <Tag color="blue" style={{ marginLeft: 8, fontSize: 12 }}>OWNER</Tag>}
                      <br />
                      <Text style={{ fontSize: 14, color: '#595959' }}>{io.description}</Text>
                    </span>
                  </MyIoCellLink>
                  <MyIoCellLink
                    to={ioPlanPath}
                    ariaLabel={`Open IO plan for ${io.order}`}
                    onActivate={goTo}
                  >
                    {io.plan_status === 'submitted' ? (
                      <Tag color="green">Submitted</Tag>
                    ) : (
                      <Tag color="gold">Pending</Tag>
                    )}
                  </MyIoCellLink>
                  <div>
                    {bridgesAccessible && io.bridge_delta != null && io.bridge_delta !== 0 ? (
                      <MyIoCellLink
                        to={bridgeDeepLinkPath(io, data.current_cycle!.id)}
                        ariaLabel={`View bridges for ${io.order}`}
                        onActivate={goTo}
                      >
                        {renderBridgeDelta(io)}
                      </MyIoCellLink>
                    ) : (
                      renderBridgeDelta(io)
                    )}
                  </div>
                  {io.one_pager_count && io.one_pager_count > 0 ? (
                    <MyIoCellLink
                      to={onePagerDeepLinkPath(io, data.current_cycle!.id)}
                      ariaLabel={`View one-pagers for ${io.order}`}
                      align="right"
                      onActivate={goTo}
                    >
                      {io.one_pager_count}
                    </MyIoCellLink>
                  ) : (
                    <Text style={{ textAlign: 'right' }}>—</Text>
                  )}
                  <Text strong style={{ textAlign: 'right' }}>{fmtK(io.total_budget ?? 0)}</Text>
                </div>
                );
              })}
            </>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No IOs assigned to you" />
          )}
        </Card>
      </div>

      {/* Bottom row (fills remaining height) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 20,
          marginTop: 20,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* My Activity */}
        <Card
          title={<><HistoryOutlined /> My Activity</>}
          style={{ borderRadius: 12, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
          styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto' } }}
        >
          {data.my_activity?.length ? (
            data.my_activity.map((a) => (
              <div key={a.id} style={{ padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {activityIcons[a.action_type] || null}
                  <Text style={{ fontSize: 14 }}>{decodeHtml(a.summary)}</Text>
                </div>
                <Text style={{ fontSize: 12, color: '#595959' }}>{formatTimeAgo(a.created_at)}</Text>
              </div>
            ))
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent activity" />
          )}
        </Card>

        {/* Needs Attention */}
        <Card
          title={<><BellOutlined /> Needs Attention</>}
          style={{ borderRadius: 12, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
          styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } }}
          extra={
            data.pending_items?.length ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {data.pending_items.length} item{data.pending_items.length !== 1 ? 's' : ''}
              </Text>
            ) : null
          }
        >
          {data.pending_items?.length ? (
            <>
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {data.pending_items.map((item, i) => {
                  const colorMap: Record<string, string> = {
                    no_plan: 'orange',
                    missing_comment: 'blue',
                    io_plan_blocked: 'orange',
                    io_plan_resubmit: 'gold',
                    io_plan_mention: 'purple',
                    io_plan_awaiting_lock: 'gold',
                    bi_mismatch_post_close: 'red',
                    variance: 'red',
                    deadline: 'gold',
                    draft_io: 'orange',
                    io_request_awaiting_manager: 'gold',
                    io_request_awaiting_finance: 'gold',
                    io_request_rejected: 'red',
                    io_request_comment: 'blue',
                    te_plan_awaiting_approval: 'gold',
                    te_plan_awaiting_finance: 'gold',
                    te_plan_rejected: 'red',
                  };
                  const resolvedPath = pendingItemPath(item);
                  const actionLabel = pendingActionLabel(item);
                  const tagLabel = pendingTagLabels[item.type] ?? item.type.replace(/_/g, ' ');
                  return (
                    <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Tag color={colorMap[item.type] || 'default'} style={{ fontSize: 12 }}>{tagLabel}</Tag>
                      <Text style={{ fontSize: 14 }}>{normalizeOpexCapexWordsInText(item.message)}</Text>
                      {resolvedPath && actionLabel && (
                        <a
                          onClick={(e) => {
                            e.preventDefault();
                            if (item.type === 'io_request_comment' && item.request_id) {
                              markRequestRead(item.request_id).catch(() => {});
                            }
                            if (item.type === 'io_plan_mention' && item.source_id && item.comment_kind) {
                              markIoPlanMentionRead(item.comment_kind, item.source_id).catch(() => {});
                            }
                            prefetchRoute(resolvedPath);
                            navigate(resolvedPath);
                          }}
                          href={resolvedPath}
                          style={{ fontSize: 12, whiteSpace: 'nowrap', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          {actionLabel} <ArrowRightOutlined style={{ fontSize: 12 }} />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<Text type="success" strong>You're all caught up!</Text>}
            />
          )}
        </Card>
      </div>

      {/* Footer sync status */}
      {data.compliance_summary && (
        <div style={{ marginTop: 24, padding: '10px 16px', background: 'rgba(0,0,0,0.03)', borderRadius: 8, fontSize: 12, color: '#595959', display: 'flex', gap: 16 }}>
          <span>Invoices: <strong>{data.compliance_summary.last_invoice_upload ? formatDateTime(data.compliance_summary.last_invoice_upload) : 'Never'}</strong></span>
          <span>Planning Import: <strong>{data.compliance_summary.last_bi_import ? formatDateTime(data.compliance_summary.last_bi_import) : 'Never'}</strong></span>
        </div>
      )}
    </div>
  );
}
